import React from 'react';
import PropTypes from 'prop-types';
import {
  compose,
  lifecycle,
  setPropTypes,
  withHandlers,
  withState
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import PublicFooter from '../../components/PublicFooter';
import { forgotPasswordSubmit } from '../../auth/Auth';
import ValidationText from '../../components/atoms/ValidationText';
import Header from '../../components/PublicHeader';
import RequiredButton from '../../components/atoms/RequiredButton';
import ProgressMobileDialog from '../../components/molecules/ProgressMobileDialog';

const styles = theme => ({
  '@global': {
    body: {
      backgroundColor: '#F7F7F7',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
    }
  },
  main: {
    flexGrow: 1,
    maxWidth: 375,
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 20
    // border: '1px solid #ccc'
  },
  page: {
    paddingLeft: 15,
    paddingRight: 15
  },
  paper: {
    display: 'flex',
    flexDirection: 'column'
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1)
  },
  titleGroup: {
    marginTop: 20,
    marginBottom: 20,
    display: 'flex',
    alignItems: 'flex-start'
  },
  pagerInDiv: {
    padding: 20
  }
});

const propTypes = {
  match: PropTypes.object.isRequired
};

const enhancer = compose(
  setPropTypes(propTypes),
  withState('cognitoId', 'setCognitoId', ''),
  withState('code', 'setCode', ''),
  withState('newPassword', 'setNewPassword', ''),
  withState('confirmNewPassword', 'setConfirmNewPassword', ''),
  withState('error', 'setError', ''),
  withState('errorCode', 'setErrorCode', ''),
  withState('errorConfirm', 'setErrorConfirm', ''),
  withState('openConfirm', 'updateOpenConfirm', false),
  withState('loading', 'updateLoading', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('titleMessage', 'setTitleMessage', ''),
  withState('showError', 'setShowError', false),
  withState('errorMessage', 'setErrorMessage', ''),
  withHandlers({
    handleRedirect: props => path => {
      props.history.push(path);
    }
  }),
  lifecycle({
    componentDidMount() {
      this.props.setCognitoId(this.props.match.params.cognitoId);
      console.log('match ===>', this.props.match.params.cognitoId);
      console.log('params ===>', this.props.location.search.substring(1));
    }
  })
);

const resetPassword = enhancer(
  ({
    classes,
    handleRedirect,
    cognitoId,
    code,
    setCode,
    newPassword,
    setNewPassword,
    error,
    setError,
    openConfirm,
    updateOpenConfirm,
    errorCode,
    setErrorCode,
    loading,
    updateLoading,
    resultMessage,
    setResultMessage,
    titleMessage,
    setTitleMessage,
    confirmNewPassword,
    setConfirmNewPassword,
    errorConfirm,
    setErrorConfirm,
    showError,
    setShowError,
    errorMessage,
    setErrorMessage
  }) => {
    const validPassword = () => {
      const value = newPassword;
      if (value === '') {
        setError('必須項目です。');
        return 1;
      }
      if (
        value.length < 8 ||
        !/[A-Z]/.test(value) ||
        !/[a-z]/.test(value) ||
        !/[0-9]/.test(value)
      ) {
        setError(
          '英大文字・英小文字・数字全てを含め、8文字以上で入力してください。'
        );
        return 1;
      }
      setError('');
      setNewPassword(value);
      return 0;
    };

    const validConfirmPassword = () => {
      const value = confirmNewPassword;
      const pass = newPassword;
      if (value === '') {
        setErrorConfirm('必須項目です。');
        return 1;
      }
      if (pass !== value) {
        setErrorConfirm('確認用パスワードが正しくありません。');
        return 1;
      }
      setErrorConfirm('');
      setConfirmNewPassword(value);
      return 0;
    };

    const validCode = () => {
      const value = code;
      if (value === '') {
        setErrorCode('必須項目です。');
        return 1;
      }
      setErrorCode('');
      return 0;
    };

    const actionConfirm = () => {
      updateOpenConfirm(false);
      handleRedirect('/');
    };

    const actionClose = () => {
      setShowError(false);
    };

    const resetPasswordSubmit = async () => {
      updateLoading(true);
      updateOpenConfirm(true);
      forgotPasswordSubmit(cognitoId, code, newPassword)
        .then(() => {
          updateLoading(false);
          setResultMessage('パスワードの設定を完了しました。');
          setTitleMessage('パスワード再設定完了');
          // handleRedirect('/reset_complete');
        })
        .catch(() => {
          // setError(requestError.message);
          updateLoading(false);
          updateOpenConfirm(false);
          setShowError(true);
          setErrorMessage(
            '認証コードが正しくありません。もう一度お試しください。'
          );
        });
    };

    const isDisableButton = () => {
      return !(
        code &&
        newPassword &&
        confirmNewPassword &&
        !error &&
        !errorCode &&
        !errorConfirm &&
        newPassword === confirmNewPassword
      );
    };

    return (
      <Container component="main" className={classes.main}>
        <CssBaseline />
        <Header />
        <div className={classes.page}>
          <div className={classes.titleGroup}>
            <Typography component="h2" variant="h5">
              パスワード再設定
            </Typography>
          </div>

          <Typography variant="p" paragraph>
            入力したメールアドレス宛にパスワード再設定用のURLを送信しました。
          </Typography>
          <Paper className={classes.paper} elevation={1}>
            <form className={classes.form} noValidate>
              <div className={classes.pagerInDiv}>
                <RequiredButton />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="code"
                  label="認証コード"
                  id="code"
                  autoFocus
                  onChange={e => {
                    setCode(e.target.value);
                  }}
                  onBlur={validCode}
                />
                <ValidationText message={errorCode} />
                <RequiredButton />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="newPassword"
                  label="新パスワード"
                  type="password"
                  id="newPassword"
                  onChange={e => {
                    setNewPassword(e.target.value);
                  }}
                  onBlur={validPassword}
                />
                <ValidationText message={error} />
                <Typography variant="p" paragraph>
                  パスワードは、英大文字・英小文字・数字全てを含め、8文字以上です
                </Typography>
                <RequiredButton />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmNewPassword"
                  label="確認用パスワード"
                  type="password"
                  id="confirmnewPassword"
                  onChange={e => {
                    setConfirmNewPassword(e.target.value);
                  }}
                  onBlur={validConfirmPassword}
                />
                <ValidationText message={errorConfirm} />
              </div>
              <Divider />
              <div className={classes.pagerInDiv}>
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  onClick={resetPasswordSubmit}
                  disabled={isDisableButton()}
                >
                  送信
                </Button>
              </div>
            </form>
            <ProgressMobileDialog
              openProgress={openConfirm}
              progressOkAction={actionConfirm}
              loading={loading}
              resultMessage={resultMessage}
              title={titleMessage}
              btnText="ログイン画面へ戻る"
            />
            <ProgressMobileDialog
              openProgress={showError}
              progressOkAction={actionClose}
              resultMessage={errorMessage}
              btnText="閉じる"
            />
          </Paper>
        </div>
        <Box mt={8}>
          <PublicFooter />
        </Box>
      </Container>
    );
  }
);

export default withRouter(withStyles(styles)(resetPassword));
